import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { extractPdfTextBrowser } from "@/lib/pdf-utils";
import { extractXlsxTextBrowser } from "@/lib/xlsx-utils";
import { compressImage } from "@/lib/image-utils";

// ⚡ CACHE de classificações (evita reprocessar textos similares)
const CACHE_KEY_PREFIX = 'smartfile_cache_';
const CACHE_EXPIRY_MS = 24 * 60 * 60 * 1000; // 24h

interface CacheEntry {
  contentType: string;
  timestamp: number;
  textHash: string;
}

// Hash simples para texto
function hashText(text: string): string {
  let hash = 0;
  for (let i = 0; i < Math.min(text.length, 1000); i++) {
    hash = ((hash << 5) - hash) + text.charCodeAt(i);
    hash = hash & hash;
  }
  return hash.toString(36);
}

// Buscar classificação no cache
function getCachedClassification(text: string): string | null {
  try {
    const textHash = hashText(text);
    const cached = localStorage.getItem(CACHE_KEY_PREFIX + textHash);
    if (!cached) return null;

    const entry: CacheEntry = JSON.parse(cached);
    const age = Date.now() - entry.timestamp;

    if (age > CACHE_EXPIRY_MS) {
      localStorage.removeItem(CACHE_KEY_PREFIX + textHash);
      return null;
    }

    console.log(`[Cache] ⚡ HIT - usando classificação cacheada: ${entry.contentType}`);
    return entry.contentType;
  } catch {
    return null;
  }
}

// Salvar classificação no cache
function cacheClassification(text: string, contentType: string): void {
  try {
    const textHash = hashText(text);
    const entry: CacheEntry = {
      contentType,
      timestamp: Date.now(),
      textHash
    };
    localStorage.setItem(CACHE_KEY_PREFIX + textHash, JSON.stringify(entry));
  } catch (error) {
    console.warn('[Cache] Failed to save:', error);
  }
}

// ⚡ PROCESSAMENTO PARALELO TOTAL
export async function processFileUploadUltraFast(
  sessionId: string,
  file: File,
  onProgress?: (stage: string, progress: number) => void,
  options?: { skipUpload?: boolean; skipAIInsights?: boolean }
): Promise<void> {
  const startTime = Date.now();

  try {
    let fileName: string;

    // FASE 1: Upload + Compressão (pular se arquivo já está no storage)
    if (options?.skipUpload) {
      onProgress?.("Usando arquivo existente...", 10);

      // Buscar file_path da sessão existente
      const { data: session } = await supabase
        .from("smartfile_sessions")
        .select("file_path")
        .eq("id", sessionId)
        .single();

      if (!session?.file_path) throw new Error("Arquivo não encontrado na sessão");
      fileName = session.file_path;

      console.log(`[Fast] ⚡ Pulando upload - usando arquivo existente: ${fileName}`);
    } else {
      onProgress?.("Enviando...", 10);

      let fileToUpload = file;
      const isImage = file.type.startsWith('image/');

      if (isImage && file.size > 2 * 1024 * 1024) {
        fileToUpload = await compressImage(file, {
          maxWidth: 1600,
          maxHeight: 1600,
          quality: 0.8,
          maxSizeMB: 1.5,
        });
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Usuário não autenticado");

      const sanitizedName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      fileName = `${user.id}/${sessionId}/${Date.now()}_${sanitizedName}`;

      console.log(`[Fast] 📁 Upload path: ${fileName} (user: ${user.id}, bucket: patient-docs)`);

      const { error: uploadError } = await supabase.storage
        .from("patient-docs")
        .upload(fileName, fileToUpload);

      if (uploadError) {
        console.error(`[Fast] ❌ Storage upload failed:`, uploadError);
        throw uploadError;
      }

      await supabase
        .from("smartfile_sessions")
        .update({
          file_name: file.name,
          file_path: fileName,
          file_type: file.type,
          file_size: fileToUpload.size,
          status: "uploaded",
        })
        .eq("id", sessionId);
    }

    // FASE 2: Extração Local com Fallback Robusto
    onProgress?.("Extraindo...", 30);

    let extractedText = "";
    let extractionMethod = "any";
    const ext = file.name.split('.').pop()?.toLowerCase() || '';

    // ⚡ Extração local para PDF com fallback
    if (ext === 'pdf' || file.type.includes('pdf')) {
      try {
        extractedText = await extractPdfTextBrowser(file);
        console.log(`[Fast] ⚡ PDF local: ${extractedText.length} chars`);
        extractionMethod = "local_pdf";

        // ✅ VALIDAÇÃO: Se extração local falhou (texto vazio/muito curto), usar edge function
        if (!extractedText || extractedText.trim().length < 50) {
          console.warn('[Fast] ⚠️ PDF local extraction failed or empty, using edge function fallback');
          const { data: extractData, error: extractError } = await supabase.functions.invoke(
            "smartfile-v15-extract-text",
            { body: { filePath: fileName, fileType: file.type, sessionId } }
          );
          if (extractError) throw extractError;
          extractedText = extractData.text;
          extractionMethod = "edge_function_fallback";
          if (extractData.aiModel) {
            console.log(`[Fast] 🤖 Extraction AI Model: ${extractData.aiModel.provider}/${extractData.aiModel.model_code} (${extractData.aiModel.name || 'N/A'})`);
          }
          console.log(`[Fast] ✅ Fallback successful: ${extractedText.length} chars`);
        }
      } catch (error) {
        console.error('[Fast] ❌ Local PDF extraction error:', error);
        // Fallback para edge function em caso de erro
        const { data: extractData, error: extractError } = await supabase.functions.invoke(
          "smartfile-v15-extract-text",
          { body: { filePath: fileName, fileType: file.type, sessionId } }
        );
        if (extractError) throw new Error(`Both local and edge extraction failed: ${extractError.message}`);
        extractedText = extractData.text;
        extractionMethod = "edge_function_error_fallback";
        if (extractData.aiModel) {
          console.log(`[Fast] 🤖 Extraction AI Model: ${extractData.aiModel.provider}/${extractData.aiModel.model_code} (${extractData.aiModel.name || 'N/A'})`);
        }
        console.log(`[Fast] ✅ Error fallback successful: ${extractedText.length} chars`);
      }
    } else if (['xlsx', 'xls'].includes(ext)) {
      try {
        extractedText = await extractXlsxTextBrowser(file);
        console.log(`[Fast] ⚡ XLSX local: ${extractedText.length} chars`);
        extractionMethod = "local_xlsx";

          // ✅ VALIDAÇÃO: Mesmo para XLSX
          if (!extractedText || extractedText.trim().length < 20) {
            console.warn('[Fast] ⚠️ XLSX local extraction failed, using edge function');
            const { data: extractData, error: extractError } = await supabase.functions.invoke(
              "smartfile-v15-extract-text",
              { body: { filePath: fileName, fileType: file.type, sessionId } }
            );
            if (extractError) throw extractError;
            extractedText = extractData.text;
            extractionMethod = "edge_function_fallback";
            if (extractData.aiModel) {
              console.log(`[Fast] 🤖 Extraction AI Model: ${extractData.aiModel.provider}/${extractData.aiModel.model_code} (${extractData.aiModel.name || 'N/A'})`);
            }
          }
      } catch (error) {
        console.error('[Fast] ❌ XLSX extraction error:', error);
        const { data: extractData, error: extractError } = await supabase.functions.invoke(
          "smartfile-v15-extract-text",
          { body: { filePath: fileName, fileType: file.type, sessionId } }
        );
        if (extractError) throw extractError;
        extractedText = extractData.text;
        extractionMethod = "edge_function_error_fallback";
        if (extractData.aiModel) {
          console.log(`[Fast] 🤖 Extraction AI Model: ${extractData.aiModel.provider}/${extractData.aiModel.model_code} (${extractData.aiModel.name || 'N/A'})`);
        }
      }
    } else {
      // Fallback: edge function (imagens, áudio, etc.)
      const { data: extractData, error: extractError } = await supabase.functions.invoke(
        "smartfile-v15-extract-text",
        { body: { filePath: fileName, fileType: file.type, sessionId } }
      );
      if (extractError) throw extractError;
      extractedText = extractData.text;
      extractionMethod = "edge_function_required";
      if (extractData.aiModel) {
        console.log(`[Fast] 🤖 Extraction AI Model: ${extractData.aiModel.provider}/${extractData.aiModel.model_code} (${extractData.aiModel.name || 'N/A'})`);
      }
    }

    // ✅ VALIDAÇÃO FINAL: Garantir que temos texto antes de prosseguir
    if (!extractedText || extractedText.trim().length < 10) {
      throw new Error(`Text extraction failed: extracted text is empty or too short (${extractedText.length} chars)`);
    }

    console.log(`[Fast] ✅ Extraction complete via ${extractionMethod}: ${extractedText.length} chars`);

    // Salvar texto extraído
    await supabase
      .from("smartfile_sessions")
      .update({
        extracted_text: extractedText,
        extraction_status: "completed", // ✅ Marcar como completado
        status: "extracted",
        step: 2,
      })
      .eq("id", sessionId);

    // FASE 3: ⚡ NOVA ABORDAGEM COM TOOLS - LLM escolhe a tool apropriada
    onProgress?.("Analisando com IA...", 50);

    // ✅ VALIDAÇÃO: Garantir que temos texto suficiente (mínimo 10 chars para aceitar entradas simples)
    if (!extractedText || extractedText.trim().length < 10) {
      throw new Error('Cannot process: extracted text is too short (minimum 10 characters)');
    }

    // Adicionar aviso para textos muito curtos
    if (extractedText.trim().length < 50) {
      console.log(`[Fast] ⚠️ Short text detected (${extractedText.trim().length} chars) - processing as simple input`);
    }

    // ✅ NOVA LÓGICA: Usar edge function com tools (AGENTE)
    // O agente executa: classificar → processar → salvar
    console.log(`[Fast] 🎯 Processing with agent tools...`);
    const { data: processData, error: processError } = await supabase.functions.invoke(
      "smartfile-v15-process-with-tools-2",
      { body: { extractedText, fileType: file.type, sessionId } }
    );

    if (processError) {
      console.error('[Fast] ❌ Process with tools error:', processError);
      throw processError;
    }

    if (!processData.success) {
      throw new Error(processData.error || 'Falha no processamento com tools');
    }

    const contentType = processData.contentType || "other";
    const records = processData.records || [];
    const toolUsed = processData.toolUsed || "any";

    // Log dos modelos de IA usados
    if (processData.aiModels) {
      console.log(`[Fast] 🤖 AI Models used:`);
      if (processData.aiModels.categorization) {
        console.log(`[Fast]   📋 Categorization: ${processData.aiModels.categorization.provider}/${processData.aiModels.categorization.model_code} (${processData.aiModels.categorization.name || 'N/A'})`);
      }
      if (processData.aiModels.outputJson) {
        console.log(`[Fast]   📄 Output JSON: ${processData.aiModels.outputJson.provider}/${processData.aiModels.outputJson.model_code} (${processData.aiModels.outputJson.name || 'N/A'})`);
      }
    }

    console.log(`[Fast] ✅ Processed using tool: ${toolUsed}`);
    console.log(`[Fast] ✅ Content type: ${contentType}, Records: ${records.length}`);

    // Cachear classificação para uso futuro
    cacheClassification(extractedText, contentType);

    // Salvar classificação e estrutura
    await supabase
      .from("smartfile_sessions")
      .update({
        content_type: contentType,
        structured_records: records,
        status: "structured",
        ai_classification: {
          method: "tools",
          tool: toolUsed,
          confidence: processData.confidence || 0.9
        }
      })
      .eq("id", sessionId);

    // ⚡ GERAR INSIGHTS E RESUMO DA IA (aguardar conclusão)
    // Verificar se AI insights devem ser desabilitados (vem do localStorage via options)
    const shouldSkipAI = options?.skipAIInsights;

    if (!shouldSkipAI) {
      onProgress?.("Gerando resumo da IA...", 80);

      try {
        const { data: insightsData, error: insightsError } = await supabase.functions.invoke(
          "smartfile-v15-generate-insights-fast",
          { body: { records, contentType, sessionId } }
        );

        if (insightsError) {
          console.warn('[Fast] Insights generation failed:', insightsError);
          // Não falhar o processo se insights falharem, apenas logar
        } else if (insightsData?.success) {
          if (insightsData.aiModel) {
            console.log(`[Fast] 🤖 Insights AI Model: ${insightsData.aiModel.provider}/${insightsData.aiModel.model_code} (${insightsData.aiModel.name || 'N/A'})`);
          }
          // Aguardar atualização do resumo da IA
          await supabase
            .from("smartfile_sessions")
            .update({
              ai_summary: insightsData.summary,
              ai_insights: insightsData.insights,
            })
            .eq("id", sessionId);

          console.log('[Fast] ✅ AI summary generated and saved');
        } else {
          console.warn('[Fast] Insights generation returned unsuccessful result');
        }
      } catch (insightsErr) {
        console.warn('[Fast] Insights generation error (non-blocking):', insightsErr);
        // Não falhar o processo se insights falharem
      }
    } else {
      console.log('[Fast] ⏭️ Skipping AI insights generation (disabled)');
    }

    const elapsed = Date.now() - startTime;
    console.log(`[Fast] ✅ COMPLETED in ${elapsed}ms`);

    onProgress?.("Pronto!", 100);

  } catch (error: any) {
    console.error('[Fast] ERROR:', error);
    await supabase
      .from("smartfile_sessions")
      .update({ status: "failed", error_message: error.message })
      .eq("id", sessionId);
    throw error;
  }
}

// ⚡ PROCESSAMENTO DE TEXTO DIRETO - Mesmo fluxo do arquivo
export async function processTextInputUltraFast(
  sessionId: string,
  text: string,
  onProgress?: (stage: string, progress: number) => void,
  options?: { skipAIInsights?: boolean }
): Promise<void> {
  const startTime = Date.now();

  try {
    // ✅ VALIDAÇÃO: Garantir que temos texto válido
    const trimmedText = text.trim();
    if (!trimmedText || trimmedText.length < 10) {
      throw new Error(`Texto muito curto (mínimo 10 caracteres). Recebido: ${trimmedText.length} caracteres`);
    }

    // FASE 1: Salvar texto diretamente (pular extração)
    onProgress?.("Salvando texto...", 10);
    
    const { error: updateError } = await supabase
      .from("smartfile_sessions")
      .update({
        extracted_text: trimmedText,
        file_name: 'entrada_manual.txt',
        file_type: 'text/plain',
        extraction_status: "completed",
        status: "extracted",
        step: 2,
      })
      .eq("id", sessionId);

    if (updateError) throw updateError;

    console.log(`[Fast] ✅ Text saved: ${trimmedText.length} chars`);

    // FASE 2: ⚡ NOVA ABORDAGEM COM TOOLS - LLM escolhe a tool apropriada
    // Mesmo fluxo usado para arquivos
    onProgress?.("Analisando com IA...", 30);

    // Adicionar aviso para textos muito curtos
    if (trimmedText.length < 50) {
      console.log(`[Fast] ⚠️ Short text detected (${trimmedText.length} chars) - processing as simple input`);
    }

    // ✅ NOVA LÓGICA: Usar edge function com tools (AGENTE)
    // O agente executa: classificar → processar → salvar
    console.log(`[Fast] 🎯 Processing text with agent tools...`);
    const { data: processData, error: processError } = await supabase.functions.invoke(
      "smartfile-v15-process-with-tools-2",
      { body: { extractedText: trimmedText, fileType: 'text/plain', sessionId } }
    );

    if (processError) {
      console.error('[Fast] ❌ Process with tools error:', processError);
      throw processError;
    }

    if (!processData.success) {
      throw new Error(processData.error || 'Falha no processamento com tools');
    }

    const contentType = processData.contentType || "other";
    const records = processData.records || [];
    const toolUsed = processData.toolUsed || "any";

    // Log dos modelos de IA usados
    if (processData.aiModels) {
      console.log(`[Fast] 🤖 AI Models used:`);
      if (processData.aiModels.categorization) {
        console.log(`[Fast]   📋 Categorization: ${processData.aiModels.categorization.provider}/${processData.aiModels.categorization.model_code} (${processData.aiModels.categorization.name || 'N/A'})`);
      }
      if (processData.aiModels.outputJson) {
        console.log(`[Fast]   📄 Output JSON: ${processData.aiModels.outputJson.provider}/${processData.aiModels.outputJson.model_code} (${processData.aiModels.outputJson.name || 'N/A'})`);
      }
    }

    console.log(`[Fast] ✅ Processed using tool: ${toolUsed}`);
    console.log(`[Fast] ✅ Content type: ${contentType}, Records: ${records.length}`);

    // Cachear classificação para uso futuro
    cacheClassification(trimmedText, contentType);

    // Salvar classificação e estrutura
    await supabase
      .from("smartfile_sessions")
      .update({
        content_type: contentType,
        structured_records: records,
        status: "structured",
        ai_classification: {
          method: "tools",
          tool: toolUsed,
          confidence: processData.confidence || 0.9
        }
      })
      .eq("id", sessionId);

    // ⚡ GERAR INSIGHTS E RESUMO DA IA (aguardar conclusão)
    // Verificar se AI insights devem ser desabilitados (vem do localStorage via options)
    const shouldSkipAI = options?.skipAIInsights;

    if (!shouldSkipAI) {
      onProgress?.("Gerando resumo da IA...", 70);

      try {
        const { data: insightsData, error: insightsError } = await supabase.functions.invoke(
          "smartfile-v15-generate-insights-fast",
          { body: { records, contentType, sessionId } }
        );

        if (insightsError) {
          console.warn('[Fast] Insights generation failed:', insightsError);
          // Não falhar o processo se insights falharem, apenas logar
        } else if (insightsData?.success) {
          if (insightsData.aiModel) {
            console.log(`[Fast] 🤖 Insights AI Model: ${insightsData.aiModel.provider}/${insightsData.aiModel.model_code} (${insightsData.aiModel.name || 'N/A'})`);
          }
          // Aguardar atualização do resumo da IA
          await supabase
            .from("smartfile_sessions")
            .update({
              ai_summary: insightsData.summary,
              ai_insights: insightsData.insights,
            })
            .eq("id", sessionId);

          console.log('[Fast] ✅ AI summary generated and saved');
        } else {
          console.warn('[Fast] Insights generation returned unsuccessful result');
        }
      } catch (insightsErr) {
        console.warn('[Fast] Insights generation error (non-blocking):', insightsErr);
        // Não falhar o processo se insights falharem
      }
    } else {
      console.log('[Fast] ⏭️ Skipping AI insights generation (disabled)');
    }

    const elapsed = Date.now() - startTime;
    console.log(`[Fast] ✅ TEXT PROCESSING COMPLETED in ${elapsed}ms`);

    onProgress?.("Pronto!", 100);

  } catch (error: any) {
    console.error('[Fast] TEXT PROCESSING ERROR:', error);
    await supabase
      .from("smartfile_sessions")
      .update({ status: "failed", error_message: error.message })
      .eq("id", sessionId);
    throw error;
  }
}

// ⚡ Limpar cache antigo (chamar ao iniciar app)
export function cleanOldCache(): void {
  try {
    const keys = Object.keys(localStorage);
    let cleaned = 0;

    keys.forEach(key => {
      if (key.startsWith(CACHE_KEY_PREFIX)) {
        try {
          const entry = JSON.parse(localStorage.getItem(key) || '');
          const age = Date.now() - entry.timestamp;
          if (age > CACHE_EXPIRY_MS) {
            localStorage.removeItem(key);
            cleaned++;
          }
        } catch {
          localStorage.removeItem(key);
          cleaned++;
        }
      }
    });

    if (cleaned > 0) {
      console.log(`[Cache] Cleaned ${cleaned} old entries`);
    }
  } catch (error) {
    console.warn('[Cache] Cleanup failed:', error);
  }
}


